import { FC, useEffect, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { IonButton, IonIcon, IonModal } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { IonModalCustomEvent, ModalBreakpointChangeEventDetail } from '@ionic/core';
import { isPlatform } from '../utils/isPlatform';
import { isAutomatedTests } from '../utils/isAutomatedTests';
import { Timer } from '../components/Timer';
import { RecordingState } from './RecordingState';
import { getRecordingStatusText, shouldOfferResume } from './RecordingSheetPresentation';
import { mediaMaxWidth } from '../theme/utils';
import { Text } from '../components/Text';
import { PauseIcon, PauseRegularIcon, PlayRegularIcon, StopIcon } from '../theme/Icons';
import { close } from 'ionicons/icons';
import { QueryParamDrivenDrawerState } from '../components/query-param-driver-drawer/QueryParamDrivenDrawerState';
import { CareMapDrawerState } from '../caremap/CareMapDrawerState';
import { NavigationMenuState } from '../navigation/NavigationMenuState';
import { RecordingTips } from './RecordingTips';
import { IntroPrompts } from './IntroPrompts';

const useStyles = createUseStyles({
    sheet: {
        // don't cover the tabbed nav
        [mediaMaxWidth.lg]: {
            bottom: 'calc(var(--ion-safe-area-bottom, 0) + 57px)'
        },

        [mediaMaxWidth.md]: {
            '--box-shadow': '0 0px 12px rgba(0, 0, 0, 0.4)'
        }
    },

    sheetContent: {
        gap: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        height: 'calc(100% - 200px)',
        paddingTop: 24
    },

    sheetContentMinimized: {
        gap: 22,
        display: 'flex'
    },

    status: {
        gap: 8,
        display: 'flex',
        alignItems: 'center'
    },

    statusIcon: {
        height: 20,
        display: 'flex',
        alignItems: 'center'
    },

    recordingDot: {
        width: 14,
        height: 14,
        borderRadius: '50%',
        background: 'var(--ion-color-danger)'
    },

    statusText: {
        fontWeight: 600
    },

    timer: {
        width: '100%',
        fontSize: 48,
        fontWeight: 650,
        display: 'flex',
        justifyContent: 'center'
    },

    buttons: {
        gap: 62,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',

        '& > div': {
            gap: 6,
            minWidth: 52,
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',

            '& ion-text': {
                fontSize: 14
            }
        },

        '& ion-button': {
            width: 42,
            height: 42,
            '--padding-end': 0,
            '--padding-start': 0
        }
    },

    saveButton: {
        width: '56px !important',
        height: '56px !important'
    },

    resumeButton: {
        '& > ion-text': {
            marginLeft: 4
        }
    }
});

const Status: FC = () => {
    const classes = useStyles();

    const recordingState = useInstance(RecordingState);
    useSubscription(() => recordingState);

    const isPaused = recordingState.isPaused();
    const isInterrupted = recordingState.isInterrupted();
    const needsManualResume = recordingState.needsManualResume();
    const isMinimized = recordingState.isSheetMinimized();

    const statusText = getRecordingStatusText({ isPaused, isInterrupted, needsManualResume, isMinimized });

    return (
        <div className={classes.status}>
            <div className={classes.statusIcon} style={isMinimized ? {marginTop: 3} : {}}>
                {isPaused || isInterrupted ? (
                    <span data-id="pause-icon">
                        <PauseIcon color="danger" />
                    </span>
                ) : (
                    <div className={classes.recordingDot} data-id="recording-dot">&nbsp;</div>
                )}
            </div>
            <div className={classes.statusText} data-id="recording-status">
                <Text>{statusText}</Text>
            </div>
        </div>
    );
};

const MIN_SHEET_BREAKPOINT = 0.07;
const MAX_SHEET_BREAKPOINT = 0.75;

export type IRecrodingSheetProps = {
    onCancel: () => void;
    onSave: () => void;
    onPause: () => void;
    onTimerPause?: (seconds: number) => void;
    onResume: () => void;
};

export const RecordingSheet: FC<IRecrodingSheetProps> = ({ onCancel, onSave, onPause, onTimerPause, onResume }) => {
    const classes = useStyles();

    const [currentBreakPoint, setCurrentBreakpoint] = useState<number>(MAX_SHEET_BREAKPOINT);

    const recordingState = useInstance(RecordingState);
    useSubscription(() => recordingState);

    const qpDrawerState = useInstance(QueryParamDrivenDrawerState);
    useSubscription(() => qpDrawerState);

    const cpDrawerState = useInstance(CareMapDrawerState);
    useSubscription(() => cpDrawerState);

    const navDrawerState = useInstance(NavigationMenuState);
    useSubscription(() => navDrawerState);

    const recordingSheetRef = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        if (recordingState.isRecording()) {
            const bp = recordingState.isSheetMinimized() ? MIN_SHEET_BREAKPOINT : MAX_SHEET_BREAKPOINT;
            recordingSheetRef.current?.setCurrentBreakpoint(bp);
            setCurrentBreakpoint(bp);
        }
    }, [recordingState, recordingState.state.isSheetMinimized]);

    const sheetBreakpointDidChange = (ev: IonModalCustomEvent<ModalBreakpointChangeEventDetail>) => {
        if (ev.detail.breakpoint === MIN_SHEET_BREAKPOINT) {
            recordingState.minimizeSheet();
        } else {
            recordingState.maximizeSheet();
        }
    };

    const isMinimized = recordingState.isSheetMinimized();
    const isDrawerOpen = qpDrawerState.isOpen() || cpDrawerState.isOpen() || navDrawerState.isOpen();

    // Must stay derived from shouldOfferResume: the status line names Resume on the same rule, and
    // recording-pause-resume.e2e.ts fails if this stops agreeing with it.
    const offerResume = shouldOfferResume({
        isPaused: recordingState.isPaused(),
        isInterrupted: recordingState.isInterrupted(),
        needsManualResume: recordingState.needsManualResume()
    });

    const recordingContext = recordingState.getCurrentRecordingContext();
    const isIntroRecording = recordingState.state.currentRecordingExtras?.summaryPrompt === 'intro';
    // Show tips even when context is undefined (menu bar recording), but not for intro recordings
    const shouldShowTips = !isIntroRecording && recordingState.shouldShowTips() && !isMinimized;

    const handleDismissTips = () => {
        recordingState.dismissTipsForSession();
    };

    return (
        <IonModal
            ref={recordingSheetRef}
            handleBehavior="cycle"
            backdropBreakpoint={1}
            backdropDismiss={false}
            className={classes.sheet}
            isOpen={recordingState.isRecording()}
            initialBreakpoint={MAX_SHEET_BREAKPOINT}
            breakpoints={[MIN_SHEET_BREAKPOINT, MAX_SHEET_BREAKPOINT]}
            onIonBreakpointDidChange={sheetBreakpointDidChange}
            style={isDrawerOpen ? {display: 'none'} : {}}
            data-id="recording-sheet"
            data-breakpoint={currentBreakPoint}
            animated={!isAutomatedTests()}
        >
            <div
                style={isPlatform('capacitor') ? {} : {paddingTop: 32, justifyContent: 'center'}}
                className={`ion-padding ${isMinimized ? classes.sheetContentMinimized : classes.sheetContent}`}
            >
                <Status />
                <div className={classes.timer} style={isMinimized ? {fontSize: 18, width: 'unset'} : {}} data-id="recording-timer">
                    <Timer
                        onPause={onTimerPause}
                        size={isMinimized ? 'small' : undefined}
                    />
                </div>
                {!isMinimized && (
                    <div className={classes.buttons} data-id="recording-controls">
                        <div>
                            <IonButton shape="round" fill="outline" color="danger" onClick={onCancel} data-id="discard-recording-btn">
                                <IonIcon icon={close} color="danger" />
                            </IonButton>
                            <Text type="subdetail">Discard</Text>
                        </div>

                        <div>
                            <IonButton shape="round" color="danger" onClick={onSave} className={classes.saveButton} data-id="stop-recording-btn">
                                <StopIcon slot="icon-only" color="light" size="xlarge" />
                            </IonButton>
                            <Text type="subdetail">Save</Text>
                        </div>

                        <div>
                            {offerResume ? (
                                <>
                                    <IonButton shape="round" fill="outline" color="danger" onClick={onResume} className={classes.resumeButton} data-id="resume-recording-btn">
                                        <PlayRegularIcon slot="icon-only" color="danger" />
                                    </IonButton>
                                    <Text type="subdetail">Resume</Text>
                                </>
                            ) : (
                                <>
                                    <IonButton shape="round" fill="outline" color="danger" onClick={onPause} data-id="pause-recording-btn">
                                        <PauseRegularIcon slot="icon-only" color="danger" />
                                    </IonButton>
                                    <Text type="subdetail">Pause</Text>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {isIntroRecording && (
                    <div style={isMinimized ? {display: 'none'} : undefined}>
                        <IntroPrompts />
                    </div>
                )}

                {shouldShowTips && (
                    <RecordingTips
                        context={recordingContext}
                        onDismiss={handleDismissTips}
                    />
                )}
            </div>
        </IonModal>
    );
};
